import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { getHrEditHistory } from "../../store/OnboardingSlice/thunk";
import {
  extractOnboardingActionData,
  hrEditHistoryIdentity,
  normalizeHrEditHistoryResponse,
  rejectMessageFromThunk,
} from "../../utils/onboardingHelpers";

/**
 * On-demand get_hr_edit_history viewer for the "Edited by HR" tag.
 * Does not touch HR Direct Edit / save_progress / approval_feedback state.
 */
const useHrEditHistoryView = ({ onboardingId, requestId } = {}) => {
  const dispatch = useDispatch();
  const [openIdentity, setOpenIdentity] = useState(null);
  const [cache, setCache] = useState({});
  const openIdentityRef = useRef(null);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    openIdentityRef.current = null;
    setOpenIdentity(null);
    setCache({});
    requestSeqRef.current += 1;
  }, [requestId, onboardingId]);

  const loadHistory = useCallback(
    async (identity, fieldKey, sectionKey, recordIndex = null) => {
      const seq = ++requestSeqRef.current;
      setCache((current) => ({
        ...current,
        [identity]: {
          status: "loading",
          error: "",
          candidateValue: "",
          changes: [],
        },
      }));

      const historyRequest = {
        onboarding_id: onboardingId,
        field_key: fieldKey,
        section_key: sectionKey,
      };
      const normalizedSection = String(sectionKey || "").trim().toLowerCase();
      if (
        normalizedSection === "education" ||
        normalizedSection === "employment"
      ) {
        historyRequest.record_index = recordIndex;
      }

      try {
        const response = await dispatch(
          getHrEditHistory(historyRequest),
        ).unwrap();

        // data may be [] (empty history) — still a valid success
        const data = extractOnboardingActionData(response);
        if (data === undefined) {
          throw new Error("Request failed");
        }

        const result = normalizeHrEditHistoryResponse(data);
        if (seq !== requestSeqRef.current) return;
        setCache((current) => ({
          ...current,
          [identity]: {
            status: "success",
            error: "",
            candidateValue: result?.candidateValue || "",
            changes: Array.isArray(result?.changes) ? result.changes : [],
            raw: result?.raw,
          },
        }));
      } catch (error) {
        if (seq !== requestSeqRef.current) return;
        setCache((current) => ({
          ...current,
          [identity]: {
            status: "error",
            error: rejectMessageFromThunk(
              error,
              "Unable to load edit history",
            ),
            candidateValue: "",
            changes: [],
          },
        }));
      }
    },
    [dispatch, onboardingId],
  );

  const toggleFieldHistory = useCallback(
    ({ sectionKey, fieldKey, recordIndex = null }) => {
      const identity = hrEditHistoryIdentity(sectionKey, fieldKey, recordIndex);
      const resolvedFieldKey = String(fieldKey || "").trim();
      if (!resolvedFieldKey) return;

      if (openIdentityRef.current === identity) {
        openIdentityRef.current = null;
        setOpenIdentity(null);
        return;
      }

      openIdentityRef.current = identity;
      setOpenIdentity(identity);
      loadHistory(identity, resolvedFieldKey, sectionKey, recordIndex);
    },
    [loadHistory],
  );

  const isHistoryOpen = useCallback(
    (sectionKey, fieldKey, recordIndex = null) =>
      openIdentity ===
      hrEditHistoryIdentity(sectionKey, fieldKey, recordIndex),
    [openIdentity],
  );

  const getHistoryState = useCallback(
    (sectionKey, fieldKey, recordIndex = null) => {
      const identity = hrEditHistoryIdentity(sectionKey, fieldKey, recordIndex);
      if (openIdentity !== identity) {
        return {
          status: "idle",
          error: "",
          candidateValue: "",
          changes: [],
        };
      }
      return (
        cache[identity] || {
          status: "loading",
          error: "",
          candidateValue: "",
          changes: [],
        }
      );
    },
    [cache, openIdentity],
  );

  return useMemo(
    () => ({
      toggleFieldHistory,
      isHistoryOpen,
      getHistoryState,
    }),
    [toggleFieldHistory, isHistoryOpen, getHistoryState],
  );
};

export default useHrEditHistoryView;
